import { cloneElement, type ReactElement } from 'react';

import { Btn } from '@/components/ri/Btn';
import { I, type IconProps } from '@/components/ri/icons';

export interface PWAInstallPromptProps {
  open: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}

const FEATURES: Array<{ icon: ReactElement<IconProps>; text: string }> = [
  { icon: I.shield as ReactElement<IconProps>, text: 'Tap to approve from Slack' },
  { icon: I.home as ReactElement<IconProps>, text: 'Launches from your home screen' },
  { icon: I.bolt as ReactElement<IconProps>, text: 'Opens full screen' },
];

export function PWAInstallPrompt({ open, onInstall, onDismiss }: PWAInstallPromptProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-label="Install Meridian"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        zIndex: 400,
        background: 'var(--surface-raised)',
        borderRadius: 18,
        boxShadow: 'var(--shadow-lg)',
        padding: 20,
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'start' }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'var(--text-primary)',
            color: 'var(--surface-base)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-serif)',
            fontWeight: 600,
            fontSize: 22,
            flex: '0 0 auto',
          }}
        >
          M
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Install Meridian</div>
          <div className="ri-small" style={{ fontSize: 12.5, marginTop: 4 }}>
            Adds Meridian to your home screen for fast access to quotes, accounts, and approvals.
          </div>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent',
            border: 0,
            color: 'var(--text-tertiary)',
            padding: 4,
            marginTop: -2,
            cursor: 'pointer',
          }}
        >
          {I.x}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <Btn variant="default" size="md" onClick={onDismiss}>
          Not now
        </Btn>
        <Btn
          variant="primary"
          full
          size="md"
          onClick={onInstall}
          leftIcon={cloneElement(I.download as ReactElement<IconProps>, { size: 16 })}
        >
          Add to home screen
        </Btn>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 14,
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        {FEATURES.map((f) => (
          <div
            key={f.text}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              textAlign: 'center',
            }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>
              {cloneElement(f.icon, { size: 18 })}
            </span>
            <span className="ri-small" style={{ fontSize: 11, lineHeight: 1.3 }}>
              {f.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
